import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

const initialState = {
  book: 'Genesis',        // Default book
  chapter: 1,             // Default chapter
  version: 'ESV',         // Default version
  verses: [],             // Array to hold verses
  loading: false,         // Loading state for fetching data
  error: null,            // Error state for any issues during the fetch
};

// Helper function to construct the API URL based on book, chapter, and version.
// Same-origin relative path: Flask serves the React build and the API from
// the same host, so hard-coding a host (or baking one in via
// REACT_APP_API_URL at build time) would misroute requests on any
// environment other than the one the bundle was built against.
const getApiUrl = ({ book, chapter, version }) =>
  `/api/search?book=${encodeURIComponent(book)}&chapter=${encodeURIComponent(chapter)}&version=${encodeURIComponent(version)}`;

// Async thunk for fetching a chapter of the Bible
export const fetchChapter = createAsyncThunk(
  'bible/fetchChapter',
  async ({ book, chapter, version }) => {
    const url = getApiUrl({ book, chapter, version });
    const response = await fetch(url);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch passage');
    }

    const data = await response.json();

    // If version is KRV, manually add verse numbers and format titles
    // if (version === 'KRV') {
    //   return formatKRVVerses(data);
    // }

    // For other versions, return data as is
    return data.map((verseData) => ({
      version: verseData.version,
      book: verseData.book,
      chapter: verseData.chapter,
      verse: verseData.verse,
      verse_id: verseData.verse_id,  // Include verse_id in the Redux state
      text: verseData.text,
      footnotes: Array.isArray(verseData.footnotes) ? verseData.footnotes : [],
      interlinear: Array.isArray(verseData.interlinear) ? verseData.interlinear : [],
    }));
  }
);

const bibleSlice = createSlice({
  name: 'bible',
  initialState,
  reducers: {
    setBook(state, action) {
      state.book = action.payload;
    },
    setChapter(state, action) {
      state.chapter = action.payload;
    },
    setVersion(state, action) {
      state.version = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChapter.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChapter.fulfilled, (state, action) => {
        state.loading = false;
        state.verses = action.payload;  // Store the verses from the backend response
      })
      .addCase(fetchChapter.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch chapter';
      });
  },
});

export const { setBook, setChapter, setVersion } = bibleSlice.actions;
export default bibleSlice.reducer;
